import { Location } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import {
	ActivatedRouteSnapshot,
	GuardsCheckEnd,
	NavigationCancel,
	NavigationEnd,
	NavigationError,
	NavigationStart,
	ResolveEnd,
	ResolveStart,
	Router,
	RoutesRecognized,
} from "@angular/router";
import { Actions, concatLatestFrom, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import { batchActions } from "@obg/ngrx-utils";
import {
	DocumentRef,
	getFragment,
	getPathName,
	getQueryParams,
	normalizeFragment,
	PlatformService,
	WindowRef,
} from "@obg/ngx.global";
import { ignoreNil } from "@obg/rxjs";
import { isEmpty, isEqual, isFunction, omit } from "lodash-es";
import { from, Observable, of } from "rxjs";
import { catchError, concatMap, debounceTime, filter, map, switchMap, take, tap } from "rxjs/operators";

import { RouteConfig, ROUTER_CONFIG, RouterConfig } from "../config/router-config.model";
import { RouterState } from "../state";

import { HistoryService } from "./history.service";
import { RouteAction } from "./route.action";
import { Route } from "./route.model";
import { RouteSelector } from "./route.selector";
import { RouteService } from "./route.service";

@Injectable()
export class RouteEffect {
	navigate$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RouteAction.navigate),
			map((action) => omit(action, "type")),
			concatMap((payload) =>
				this.runNavigation(() => this.routeService.navigate(payload), !!payload.extras?.state?.queueNavigation)
			)
		)
	);

	redirect$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(RouteAction.redirect),
				map((action) => omit(action, "type")),
				switchMap((payload) => this.routeService.navigateByRedirect(payload))
			),
		{ dispatch: false }
	);

	navigateByCommand$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RouteAction.navigateByCommand),
			map((action) => omit(action, "type")),
			concatMap((payload) =>
				this.runNavigation(
					() => this.routeService.navigateByCommand(payload),
					!!payload.extras?.state?.queueNavigation
				)
			)
		)
	);

	navigateByUrl$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RouteAction.navigateByUrl),
			map((action) => omit(action, "type")),
			concatMap((payload) =>
				this.runNavigation(
					() => this.routeService.navigateByUrl(payload),
					!!payload.extras?.state?.queueNavigation
				)
			)
		)
	);

	updateNavigationDepth$ = createEffect(
		() =>
			this.router.events.pipe(
				filter((event) => event instanceof ResolveStart),
				map(() => this.router.getCurrentNavigation()),
				ignoreNil(),
				tap((navigation) => {
					if (navigation.trigger === "popstate" && navigation.previousNavigation) {
						const isForwardNavigation =
							this.navigationIdReferences[navigation.id] >
							this.navigationIdReferences[navigation.previousNavigation.id];

						// Although forward button creates "popstate" navigation, it should increase depth.
						this.navigationDepth += isForwardNavigation ? 1 : -1;
						return;
					}

					// URL replacement doesn't push anything to history. So depth shouldn't be increased.
					if (navigation.extras?.replaceUrl !== true) {
						this.navigationDepth++;
					}
				})
			),
		{ dispatch: false }
	);

	replacePath$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(RouteAction.replacePath),
				tap(({ externalUrl }) => this.routeService.replacePath(externalUrl))
			),
		{ dispatch: false }
	);

	back$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RouteAction.back),
			concatLatestFrom(() => this.store.select(RouteSelector.getCurrent)),
			map(([{ fallbackUrl }, route]) => {
				const fallbackRoute = route?.fallbackRoute ?? this.config.defaultFallbackRoute;
				const isBackNavigationByHistory = this.navigationDepth > 0 || (!fallbackUrl && !fallbackRoute);
				if (isBackNavigationByHistory) {
					this.historyService.back();
				}
				return { isBackNavigationByHistory, fallbackUrl, fallbackRoute, params: route?.params };
			}),
			filter(({ isBackNavigationByHistory }) => !isBackNavigationByHistory),
			map(({ fallbackUrl, fallbackRoute, params }) => {
				if (fallbackUrl) {
					return RouteAction.navigateByUrl(fallbackUrl, { replaceUrl: true });
				}

				// replace current route with the fallback when there is no history (fake back navigation)
				this.navigationDepth = 0;

				// if `fallbackUrl` is undefined, `fallbackRoute` cannot be undefined because of previous check
				return RouteAction.navigate(fallbackRoute!, params, { replaceUrl: true });
			})
		)
	);

	initialBack$ = createEffect(
		() =>
			this.router.events.pipe(
				filter((event) => event instanceof NavigationEnd),
				take(1),
				tap(() => this.historyService.appInitialized())
			),
		{ dispatch: false }
	);

	navigationStart$ = createEffect(() =>
		this.router.events.pipe(
			filter((event) => event instanceof NavigationStart),
			tap(
				(event: NavigationStart) =>
					(this.navigationIdReferences[event.id] =
						event.restoredState && event.navigationTrigger === "popstate"
							? this.navigationIdReferences[event.restoredState.navigationId]
							: event.id)
			),
			map(({ url }: NavigationStart) => RouteAction.navigationStart({ url }))
		)
	);

	guardsCheckEnd$ = createEffect(() =>
		this.router.events.pipe(
			filter((event) => event instanceof GuardsCheckEnd),
			map((event: GuardsCheckEnd) =>
				RouteAction.guardsCheckEnd({
					shouldActivate: event.shouldActivate,
				})
			)
		)
	);

	navigationCancel$ = createEffect(() =>
		this.router.events.pipe(
			filter((event) => event instanceof NavigationCancel),
			map(() => RouteAction.navigationCancel())
		)
	);

	navigationError$ = createEffect(() =>
		this.router.events.pipe(
			filter((event) => event instanceof NavigationError),
			map(() => RouteAction.navigationError())
		)
	);

	navigationEnd$ = createEffect(() =>
		this.router.events.pipe(
			filter((event) => event instanceof NavigationEnd),
			concatLatestFrom(() => this.store.select(RouteSelector.getCurrent)),
			map(([event, currentRoute]: [NavigationEnd, Route | undefined]) => {
				const externalUrl = this.location.prepareExternalUrl(event.url);
				if (currentRoute && externalUrl !== currentRoute.externalUrl) {
					const actions = [];
					const newQueryParams = getQueryParams(externalUrl);
					if (!isEqual(currentRoute.queryParams, newQueryParams)) {
						actions.push(RouteAction.setQueryParamsSuccess(newQueryParams));
					}
					const newFragment = getFragment(externalUrl);
					if (newFragment && !isEqual(currentRoute.fragment, newFragment)) {
						actions.push(RouteAction.setFragment({ fragment: newFragment, replaceUrl: !isEmpty(actions) }));
					}
					return batchActions(...actions, RouteAction.navigationEnd());
				}

				return RouteAction.navigationEnd();
			})
		)
	);

	resolveEnd$ = createEffect(() =>
		this.router.events.pipe(
			filter((event) => event instanceof ResolveEnd),
			map((event: ResolveEnd) => this.getActivatedRouteSnapshot(event)),
			ignoreNil(),
			map((activatedRouteSnapshot) => {
				const snapshot = activatedRouteSnapshot!;
				const currentNavigation = this.router.getCurrentNavigation();
				const config = snapshot.routeConfig as RouteConfig;
				const externalUrl = this.routeService.generateUrlFromSnapshot(snapshot);
				return RouteAction.resolveEnd({
					current: {
						name: config.name === "**" ? this.config.unknownRoute : config.name,
						externalUrl,
						pathName: getPathName(externalUrl),
						params: snapshot.params,
						queryParams: snapshot.queryParams,
						data: config.data || {},
						fragment: snapshot.fragment || undefined,
						fallbackRoute: config.fallbackRoute,
						layout: config.layout,
						extras: currentNavigation ? currentNavigation.extras : undefined,
					},
					hasHistory: this.navigationDepth > 0,
				});
			})
		)
	);

	resetFragment$ = createEffect(() =>
		this.routeService.onPopState$().pipe(
			concatLatestFrom(() => this.store.select(RouteSelector.getCurrent)),
			map(([, route]) => {
				const url = this.location.path(true);
				const pathName = getPathName(url);
				return {
					isSameRoute: !!route && pathName === this.location.normalize(route.pathName),
					newFragment: normalizeFragment(url, true),
					oldFragment: normalizeFragment(route?.fragment),
				};
			}),
			filter(({ isSameRoute, newFragment, oldFragment }) => isSameRoute && newFragment !== oldFragment),
			map(({ newFragment }) => RouteAction.setFragment({ fragment: newFragment, replaceUrl: true }))
		)
	);

	changeFragment$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RouteAction.setFragment),
			map((action) => omit(action, "type")),
			tap((payload) =>
				this.routeService.setFragment(payload).then((navigationResult) => {
					if (navigationResult && !payload.replaceUrl) {
						this.navigationDepth++;
					}
				})
			),
			filter((payload) => !payload.skipScroll),
			map(() => RouteAction.scrollToCurrentFragment())
		)
	);

	scrollToFragment$ = this.platformService.forClient(() =>
		createEffect(
			() =>
				this.actions$.pipe(
					ofType(RouteAction.scrollToCurrentFragment),
					// debounce to read the dom once and make sure that the page is rendered, and the content is present
					debounceTime(20),
					map(() => {
						try {
							const hash = this.windowRef.native.location.hash;
							if (hash) {
								return decodeURIComponent(hash);
							}
						} catch {
							return undefined;
						}
						return undefined;
					}),
					filter((hash) => !!hash),
					tap((hash) => {
						const fragment = normalizeFragment(hash);
						const element = this.documentRef.native.querySelector(`[name='${fragment}'],[id='${hash}']`);
						if (element) {
							element.scrollIntoView();
						}
					})
				),
			{ dispatch: false }
		)
	);

	setQueryParams$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RouteAction.setQueryParams, RouteAction.updateQueryParams),
			map((action) => {
				const { type, ...payload } = action;
				return { payload, shouldMerge: RouteAction.updateQueryParams.type === type };
			}),
			concatMap(({ payload, shouldMerge }) =>
				this.runNavigation(
					() =>
						this.routeService.setQueryParams(payload, shouldMerge).then((navigationResult) => {
							// if navigation was successful and URL was not replaced, history entry was made
							if (navigationResult && !payload.replaceUrl) {
								this.navigationDepth++;
							}
							return navigationResult;
						}),
					!!payload.extras?.state?.queueNavigation
				)
			)
		)
	);

	logEvents$ = createEffect(
		() =>
			this.router.events.pipe(
				filter(() =>
					isFunction(this.config.enableTracing) ? this.config.enableTracing() : !!this.config.enableTracing
				),
				tap((e) => {
					/* eslint-disable no-console */
					console.groupCollapsed(`Router Event: ${(e.constructor as any).name}`);
					if (e instanceof NavigationError) {
						console.error(e.toString());
						console.error(e);
					} else {
						console.log(e.toString());
						console.log(e);
					}
					console.groupEnd();
					/* eslint-enable no-console */
				})
			),
		{ dispatch: false }
	);

	setAdditionalDataAndNavigate$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RouteAction.setAdditionalDataAndNavigate),
			map(({ url, extras }) => RouteAction.navigateByUrl(url, extras))
		)
	);

	private navigationDepth = 0;
	private navigationIdReferences: Record<number, number> = {};

	constructor(
		private store: Store<RouterState>,
		private router: Router,
		private actions$: Actions,
		private routeService: RouteService,
		private location: Location,
		private windowRef: WindowRef,
		private documentRef: DocumentRef,
		private historyService: HistoryService,
		private platformService: PlatformService,
		@Inject(ROUTER_CONFIG) private config: RouterConfig
	) {
		// @ts-ignore: private option not yet exposed for public use
		// TODO: not internal from Angular 13, remove ts-ignore
		this.router.canceledNavigationResolution = "computed";
	}

	private getActivatedRouteSnapshot(event: RoutesRecognized | ResolveEnd): ActivatedRouteSnapshot | null {
		let snapshot = event.state.root.firstChild;

		while (snapshot?.firstChild) {
			snapshot = snapshot.firstChild;
		}

		return snapshot;
	}

	private runNavigation(
		navigationPromiseFactory: () => Promise<boolean>,
		queueNavigation: boolean
	): Observable<Action> {
		return this.store.select(RouteSelector.isNavigating).pipe(
			filter((isNavigating) => !isNavigating || !queueNavigation),
			take(1),
			switchMap(() =>
				from(navigationPromiseFactory()).pipe(
					filter((value) => !value),
					map(() => RouteAction.navigationCancel()),
					catchError(() => of(RouteAction.navigationCancel()))
				)
			)
		);
	}
}
